/**
 * Parse CSV — ignore les lignes vides et les commentaires (#).
 * @param {string} text
 * @returns {{ headers: string[], rows: Record<string, string>[] }}
 */
export function parseCsv(text) {
  const lines = text
    .trim()
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0 && !l.startsWith("#"));
  if (lines.length < 2) {
    throw new Error("CSV invalide : en-tête + au moins une ligne requise.");
  }
  const headers = splitCsvLine(lines[0]).map((h) => h.trim().toLowerCase());
  const rows = lines.slice(1).map((line, idx) => {
    const cells = splitCsvLine(line);
    if (cells.length !== headers.length) {
      throw new Error(`Ligne ${idx + 2} : nombre de colonnes incorrect (${cells.length} vs ${headers.length}).`);
    }
    /** @type {Record<string, string>} */
    const row = {};
    headers.forEach((h, i) => {
      row[h] = cells[i]?.trim() ?? "";
    });
    return row;
  });
  return { headers, rows };
}

function splitCsvLine(line) {
  const out = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (ch === "," && !inQuotes) {
      out.push(cur);
      cur = "";
      continue;
    }
    cur += ch;
  }
  out.push(cur);
  return out;
}

export const DOJO_REQUIRED = ["id", "nom", "ville", "adresse", "telephone", "courriel", "sensei_id", "statut"];
export const INSTRUCTEUR_REQUIRED = ["id", "nom", "titre", "dojo_id", "courriel", "telephone", "statut"];
export const ETUDIANT_REQUIRED = ["id", "prenom", "nom", "dojo_id", "groupe", "ceinture", "statut"];

const REQUIRED_BY_TYPE = {
  dojos: DOJO_REQUIRED,
  instructeurs: INSTRUCTEUR_REQUIRED,
  etudiants: ETUDIANT_REQUIRED,
};

function assertHeaders(headers, required, label) {
  for (const col of required) {
    if (!headers.includes(col)) {
      throw new Error(`${label} : colonne manquante « ${col} ».`);
    }
  }
}

/** @param {Record<string, string>[]} rows */
export function mapDojoRows(rows) {
  return rows.map((r) => ({
    id: r.id,
    nom: r.nom,
    ville: r.ville,
    adresse: r.adresse,
    telephone: r.telephone,
    courriel: r.courriel,
    sensei_id: r.sensei_id || null,
    statut: normalizeStatut(r.statut),
    demo: String(r.demo || "").toLowerCase() === "true" || r.nom.includes("[DÉMO]"),
  }));
}

export function mapInstructeurRows(rows) {
  return rows.map((r) => ({
    id: r.id,
    nom: r.nom,
    titre: r.titre,
    dojo_id: r.dojo_id || null,
    courriel: r.courriel,
    telephone: r.telephone,
    statut: normalizeStatut(r.statut),
    demo: String(r.demo || "").toLowerCase() === "true" || r.nom.includes("[DÉMO]"),
  }));
}

export function mapEtudiantRows(rows) {
  return rows.map((r) => ({
    id: r.id,
    prenom: r.prenom,
    nom: r.nom,
    dojo_id: r.dojo_id,
    groupe: r.groupe,
    ceinture: r.ceinture,
    statut: normalizeStatut(r.statut),
    demo: String(r.demo || "").toLowerCase() === "true" || r.prenom.includes("[DÉMO]") || r.nom.includes("[DÉMO]"),
  }));
}

function normalizeStatut(s) {
  const v = (s || "a_confirmer").toLowerCase();
  if (v === "actif" || v === "inactif" || v === "a_confirmer") return v;
  return "a_confirmer";
}

const MAPPERS = {
  dojos: mapDojoRows,
  instructeurs: mapInstructeurRows,
  etudiants: mapEtudiantRows,
};

/**
 * @param {'dojos'|'instructeurs'|'etudiants'} type
 * @param {string} csvText
 */
export function importCsv(type, csvText) {
  const { headers, rows } = parseCsv(csvText);
  const required = REQUIRED_BY_TYPE[type];
  if (!required) throw new Error(`Type import inconnu : ${type}`);
  assertHeaders(headers, required, type);
  return MAPPERS[type](rows);
}

/**
 * @typedef {Object} ImportContext
 * @property {string[]} [dojoIds]
 * @property {string[]} [instructeurIds]
 */

/**
 * Aperçu import avec erreurs et avertissements.
 * @param {'dojos'|'instructeurs'|'etudiants'} type
 * @param {string} csvText
 * @param {ImportContext} [context]
 */
export function previewCsvImport(type, csvText, context = {}) {
  /** @type {string[]} */
  const errors = [];
  /** @type {string[]} */
  const warnings = [];
  /** @type {object[]} */
  let rows = [];

  if (!csvText.trim()) {
    return { rows, errors: ["CSV vide."], warnings, valid: false };
  }

  try {
    rows = importCsv(type, csvText);
  } catch (e) {
    errors.push(e instanceof Error ? e.message : String(e));
    return { rows, errors, warnings, valid: false };
  }

  const ids = new Set();
  rows.forEach((row, idx) => {
    const line = idx + 2;
    if (!row.id) errors.push(`Ligne ${line} : id obligatoire.`);
    if (ids.has(row.id)) errors.push(`Ligne ${line} : id dupliqué « ${row.id} ».`);
    ids.add(row.id);
    if (!row.nom && type !== "etudiants") errors.push(`Ligne ${line} : nom obligatoire.`);
    if (type === "etudiants" && (!row.prenom || !row.nom)) {
      errors.push(`Ligne ${line} : prénom et nom obligatoires.`);
    }
  });

  if (type === "dojos") {
    rows.forEach((row, idx) => {
      if (row.sensei_id && context.instructeurIds && !context.instructeurIds.includes(row.sensei_id)) {
        warnings.push(
          `Ligne ${idx + 2} : sensei_id « ${row.sensei_id} » inconnu — importer les instructeurs d'abord ou laisser vide.`,
        );
      }
    });
  }

  if (type === "instructeurs") {
    rows.forEach((row, idx) => {
      if (row.dojo_id && context.dojoIds && !context.dojoIds.includes(row.dojo_id)) {
        warnings.push(`Ligne ${idx + 2} : dojo_id « ${row.dojo_id} » inconnu — importer les dojos d'abord.`);
      }
    });
  }

  if (type === "etudiants") {
    rows.forEach((row, idx) => {
      if (!row.dojo_id) {
        errors.push(`Ligne ${idx + 2} : dojo_id obligatoire pour un étudiant.`);
      } else if (context.dojoIds && !context.dojoIds.includes(row.dojo_id)) {
        warnings.push(`Ligne ${idx + 2} : dojo_id « ${row.dojo_id} » inconnu — importer les dojos d'abord.`);
      }
    });
  }

  const demoCount = rows.filter((r) => r.demo).length;
  const officialCount = rows.length - demoCount;
  if (officialCount === 0 && rows.length > 0) {
    warnings.push("Toutes les lignes sont marquées [DÉMO] ou demo=true — aucune donnée officielle dans cet import.");
  }

  return {
    rows,
    errors,
    warnings,
    valid: errors.length === 0,
    stats: { total: rows.length, demo: demoCount, official: officialCount },
  };
}

/**
 * Fusionne import dans collection existante (par id).
 * @param {object[]} existing
 * @param {object[]} imported
 * @param {{ keepDemo?: boolean }} [opts]
 */
export function mergeById(existing, imported, opts = {}) {
  const map = new Map(existing.map((r) => [r.id, r]));
  for (const row of imported) {
    map.set(row.id, row);
  }
  let result = [...map.values()];
  if (opts.keepDemo === false) {
    result = result.filter((r) => !r.demo);
    result.push(...imported);
    const finalMap = new Map();
    for (const r of result) finalMap.set(r.id, r);
    result = [...finalMap.values()];
  }
  return result;
}

/**
 * Remplace les entrées non-démo par l'import ; conserve les [DÉMO] existantes sauf si même id.
 * @param {object[]} existing
 * @param {object[]} imported
 */
export function replaceOfficialKeepDemo(existing, imported) {
  const importedIds = new Set(imported.map((r) => r.id));
  const keptDemo = existing.filter((r) => r.demo && !importedIds.has(r.id));
  const keptNonDemoNotInImport = existing.filter((r) => !r.demo && !importedIds.has(r.id));
  return [...keptDemo, ...keptNonDemoNotInImport, ...imported];
}

export const CSV_TEMPLATES = {
  dojos: DOJO_REQUIRED,
  instructeurs: INSTRUCTEUR_REQUIRED,
  etudiants: ETUDIANT_REQUIRED,
};

export const IMPORT_MODES = {
  replace: "Remplacer toute la collection",
  merge: "Fusionner par id (mettre à jour / ajouter)",
  replace_official: "Importer officiel — conserver [DÉMO] existantes",
};
